import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  productVarientId: "",
  attributeId: "",
  attributeValueId: ""
});

export function ProductVarientOptionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");

  const [varients, setVarients] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [attributeValues, setAttributeValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadLookups = async () => {
    setLoading(true);
    setError(null);
    const [vRes, aRes, avRes] = await Promise.all([
      apiGet("/api/ProductVarients"),
      apiGet("/api/Attributes"),
      apiGet("/api/AttributeValues")
    ]);
    if (vRes.ok) setVarients(vRes.data);
    if (aRes.ok) setAttributes(aRes.data);
    if (avRes.ok) setAttributeValues(avRes.data);
    if (!vRes.ok) setError(vRes.error ?? "Failed to load product varients");
    else if (!aRes.ok) setError(aRes.error ?? "Failed to load attributes");
    else if (!avRes.ok) setError(avRes.error ?? "Failed to load attribute values");
    setLoading(false);
  };

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await apiGet(`/api/ProductVarientOptions/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product varient option");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        productVarientId: x.productVarientId ?? "",
        attributeId: x.attributeId ?? "",
        attributeValueId: x.attributeValueId ?? ""
      });
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.productVarientId || !form.attributeId || !form.attributeValueId) return;
    setSaving(true);
    setError(null);
    const res = await apiPost("/api/ProductVarientOptions", {
      id: editingId ?? 0,
      productVarientId: Number(form.productVarientId),
      attributeId: Number(form.attributeId),
      attributeValueId: Number(form.attributeValueId)
    });
    if (!res.ok) setError(res.error ?? "Failed to save product varient option");
    else {
      await loadLookups();
      resetForm();
    }
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    await loadLookups();
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/ProductVarientOptions/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product varient option");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        productVarientId: x.productVarientId ?? "",
        attributeId: x.attributeId ?? "",
        attributeValueId: x.attributeValueId ?? ""
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Varient Option" : "Add Varient Option"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="product-varient-options-form"
            disabled={saving}
            title="Save"
            aria-label="Save varient option"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/product-varient-options-list" className="icon-button icon-button--toolbar" title="Varient options list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>
      <form id="product-varient-options-form" onSubmit={onSubmit} className="card-body form form-fields-bottomline">
        <label className="field">
          <span>Product Varient</span>
          <select
            value={form.productVarientId}
            onChange={(e) => setForm((p) => ({ ...p, productVarientId: e.target.value ? Number(e.target.value) : "" }))}
          >
            <option value="">Select varient</option>
            {varients.map((v) => (
              <option key={v.id} value={v.id}>
                {v.skU || `Varient #${v.id}`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Attribute</span>
          <select value={form.attributeId} onChange={(e) => setForm((p) => ({ ...p, attributeId: e.target.value ? Number(e.target.value) : "" }))}>
            <option value="">Select attribute</option>
            {attributes.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name || `Attribute #${a.id}`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Attribute Value</span>
          <select
            value={form.attributeValueId}
            onChange={(e) => setForm((p) => ({ ...p, attributeValueId: e.target.value ? Number(e.target.value) : "" }))}
          >
            <option value="">Select attribute value</option>
            {attributeValues.map((a) => (
              <option key={a.id} value={a.id}>
                {a.value || `Value #${a.id}`}
              </option>
            ))}
          </select>
        </label>
        {error && <div className="error-banner">{error}</div>}
      </form>
    </div>
  );
}
